<template>
  <slot name="header">
    <Demo color="red" height="30px" name="header" />
  </slot>

  <Container class="bg-surface-default-low pb-18 md:pb-48">
    <slot name="breadcrumb">
      <Demo color="blue" height="60px" name="breadcrumb" />
    </slot>

    <slot v-if="isOutOfStock" name="out-of-stock">
      <Demo color="green" height="60px" name="out-of-stock" />
    </slot>

    <section
      v-if="!isOutOfStock"
      class="mt-12 flex flex-col flex-wrap md:mt-32 md:flex-row md:flex-nowrap md:justify-evenly"
    >
      <div
        class="relative ml-0 mr-auto w-full max-w-full grow md:w-1/2 md:max-w-[min(33.33%,416px)] lg:max-w-[min(50%,560px)]"
      >
        <slot name="product-visuals">
          <Demo color="green" height="60px" name="ProductVisuals" />
        </slot>
      </div>
      <TheMainContent
        class="min-w-0 w-full max-w-full grow px-24 md:w-2/3 md:basis-2/3 lg:w-1/2 lg:basis-1/2 lg:pe-0 lg:ps-64"
      >
        <ColumnsReversedOnDesktop class="mb-16">
          <slot name="product-summary">
            <Demo color="pink" height="60px" name="ProductSummary" />
          </slot>
        </ColumnsReversedOnDesktop>
        <RevDivider class="my-16 md:hidden" />
        <slot name="product-aside-details">
          <Demo color="pink" height="60px" name="ProductAsideDetails" />
        </slot>
      </TheMainContent>
    </section>
  </Container>

  <slot name="full-width-pre-content">
    <Demo color="purple" height="60px" name="full-width-pre-content" />
  </slot>

  <div class="bg-surface-default-low">
    <RevContainer class="pb-32 [&>*]:min-w-0 [&>*]:space-y-32">
      <slot name="content">
        <Demo color="purple" height="60px" name="content" />
      </slot>
    </RevContainer>
  </div>

  <div
    class="overflow-hidden pb-32 bg-surface-default-mid hidden has-[[data-test=container-wrapper]_>_*]:block"
  >
    <Container inner-class="px-24 space-y-32">
      <slot name="full-width-post-content">
        <Demo color="purple" height="60px" name="content" />
      </slot>
    </Container>
  </div>

  <div
    class="overflow-hidden pb-32 bg-surface-default-hi hidden has-[[data-test=container-wrapper]_>_*]:block"
  >
    <Container inner-class="px-24 space-y-32">
      <slot name="full-width-seo-content">
        <Demo color="purple" height="60px" name="content" />
      </slot>
    </Container>
  </div>

  <ClientOnly>
    <slot name="modals" />
  </ClientOnly>

  <ProductPageQATranslationsProbe />
</template>

<script setup lang="ts">
import type { VNode } from 'vue'

import { RevContainer } from '@ds/components/Container'
import { RevDivider } from '@ds/components/Divider'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import ColumnsReversedOnDesktop from '~/scopes/product-core/components/presentationals/ColumnsReversedOnDesktop/ColumnsReversedOnDesktop.vue'
import Container from '~/scopes/product-core/components/presentationals/Container/Container.vue'
import ProductPageQATranslationsProbe from '~/scopes/product-core/components/presentationals/ProductPageQATranslationsProbe/ProductPageQATranslationsProbe.vue'
import { useFetchPickers } from '~/scopes/product-core/composables/useFetch/useFetchPickers/useFetchPickers'

import Demo from './components/Demo.vue'

defineSlots<{
  /** Header */
  header: () => VNode
  /** Breadcrumb */
  breadcrumb: () => VNode
  /** Out of stock content */
  ['out-of-stock']: () => VNode
  /** Gallery and content in carousel */
  ['product-visuals']: () => VNode
  /** Reversed content on desktop layout. */
  ['product-summary']: () => VNode
  /** Content below the product summary */
  ['product-aside-details']: () => VNode
  /** Full width content before the main content */
  ['full-width-pre-content']: () => VNode
  /** Main content */
  content: () => VNode
  /** After main content (full width): Recommendation, reviews etc. */
  ['full-width-post-content']: () => VNode
  /** Post content (full width): SEO links, tech specs, articles, etc. */
  ['full-width-seo-content']: () => VNode
  /** Client-only Modals (nothing should be rendered here) */
  modals: () => VNode
}>()

const { isOutOfStock } = useFetchPickers()
</script>
